import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Box, IconButton, Tooltip } from '@mui/material';
import OpenInFullOutlinedIcon from '@mui/icons-material/OpenInFullOutlined';
import { fixLeafletIcon, MAP_SETTINGS } from '../../../utils/nsirMapConfig';
import DialogDefaultV2 from '../DialogDefaultV2';

fixLeafletIcon();

const FOCUS_ZOOM = 16;
const MAP_RADIUS = 0.5;

const buildPopupHtml = (point) => {
  if (!point.endereco) return point.title;
  return `${point.title}<br/><span class="geo-point-map-popup-address">${point.endereco}</span>`;
};

const GeoPointMapCanvas = ({
  points,
  active,
  height,
  focusId,
  onFocusComplete,
  scrollWheelZoom,
  dragging,
}) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersByIdRef = useRef({});

  useEffect(() => {
    if (!active) return undefined;
    const el = containerRef.current;
    if (!el || mapRef.current) return undefined;

    const map = L.map(el, {
      center: MAP_SETTINGS.center,
      zoom: MAP_SETTINGS.initialZoom,
      minZoom: MAP_SETTINGS.minZoom,
      maxZoom: MAP_SETTINGS.maxZoom,
      attributionControl: false,
      scrollWheelZoom,
      dragging,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '',
    }).addTo(map);

    mapRef.current = map;

    const resizeObserver = new ResizeObserver(() => {
      map.invalidateSize();
    });
    resizeObserver.observe(el);

    return () => {
      resizeObserver.disconnect();
      map.remove();
      mapRef.current = null;
      markersByIdRef.current = {};
    };
  }, [active, dragging, scrollWheelZoom]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !active) return;

    Object.values(markersByIdRef.current).forEach((marker) => marker.remove());
    markersByIdRef.current = {};

    if (!points.length) return;

    const bounds = L.latLngBounds([]);
    points.forEach((point) => {
      const marker = L.marker([point.lat, point.lng]).addTo(map);
      marker.bindPopup(buildPopupHtml(point), {
        className: 'geo-point-map-popup',
        closeButton: true,
      });
      markersByIdRef.current[point.id] = marker;
      bounds.extend([point.lat, point.lng]);
    });

    requestAnimationFrame(() => {
      map.invalidateSize();
      if (points.length === 1) {
        map.setView([points[0].lat, points[0].lng], FOCUS_ZOOM);
      } else {
        map.fitBounds(bounds, { padding: [40, 40] });
      }
    });
  }, [points, active]);

  useEffect(() => {
    if (!active) return undefined;
    const map = mapRef.current;
    if (!map) return undefined;
    const timers = [0, 120, 320].map((delay) => setTimeout(() => {
      map.invalidateSize();
    }, delay));
    return () => timers.forEach(clearTimeout);
  }, [active, height]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !active || !focusId) return;

    const marker = markersByIdRef.current[focusId];
    if (!marker) {
      onFocusComplete?.();
      return;
    }

    map.flyTo(marker.getLatLng(), FOCUS_ZOOM, { duration: 0.4 });
    marker.openPopup();
    onFocusComplete?.();
  }, [focusId, active, onFocusComplete]);

  return (
    <Box
      ref={containerRef}
      sx={(theme) => ({
        height,
        width: '100%',
        '& .leaflet-container': {
          height: '100%',
          width: '100%',
          borderRadius: MAP_RADIUS,
        },
        '& .leaflet-control-attribution': {
          display: 'none',
        },
        '& .geo-point-map-popup .leaflet-popup-content-wrapper': {
          backgroundColor: theme.palette.background.paper,
          borderRadius: 2,
          boxShadow: theme.shadows[2],
        },
        '& .geo-point-map-popup .leaflet-popup-content': {
          color: theme.palette.text.primary,
          fontSize: '12px',
          fontWeight: 600,
          lineHeight: 1.35,
          margin: '8px 10px',
          maxWidth: 260,
          whiteSpace: 'normal',
        },
        '& .geo-point-map-popup .geo-point-map-popup-address': {
          color: theme.palette.text.secondary,
          display: 'block',
          fontSize: '11px',
          fontWeight: 400,
          marginTop: '4px',
        },
        '& .geo-point-map-popup .leaflet-popup-tip': {
          backgroundColor: theme.palette.background.paper,
        },
      })}
    />
  );
};

GeoPointMapCanvas.propTypes = {
  points: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    lat: PropTypes.number.isRequired,
    lng: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    endereco: PropTypes.string,
  })).isRequired,
  active: PropTypes.bool.isRequired,
  height: PropTypes.number.isRequired,
  focusId: PropTypes.string,
  onFocusComplete: PropTypes.func,
  scrollWheelZoom: PropTypes.bool,
  dragging: PropTypes.bool,
};

GeoPointMapCanvas.defaultProps = {
  focusId: null,
  onFocusComplete: null,
  scrollWheelZoom: true,
  dragging: true,
};

const GeoPointMap = ({
  points,
  active,
  height,
  focusId,
  onFocusComplete,
  scrollWheelZoom,
  dragging,
  expandable,
}) => {
  const [expanded, setExpanded] = useState(false);
  const canvasProps = {
    points,
    active,
    height,
    focusId,
    onFocusComplete,
    scrollWheelZoom,
    dragging,
  };

  if (!expandable) {
    return <GeoPointMapCanvas {...canvasProps} />;
  }

  return (
    <>
      <Box sx={{ position: 'relative' }}>
        <Tooltip title="Ampliar mapa">
          <IconButton
            size="small"
            onClick={() => setExpanded(true)}
            aria-label="Ampliar mapa"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 1100,
              bgcolor: 'background.paper',
              boxShadow: 1,
              '&:hover': { bgcolor: 'background.paper' },
            }}
          >
            <OpenInFullOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <GeoPointMapCanvas {...canvasProps} />
      </Box>
      <DialogDefaultV2
        open={expanded}
        handlerCancel={() => setExpanded(false)}
        title="Mapa"
        maxWidth="lg"
        fullWidth
        readOnly
        zIndex={1400}
      >
        {expanded && (
          <GeoPointMapCanvas
            {...canvasProps}
            active={expanded}
            height={560}
          />
        )}
      </DialogDefaultV2>
    </>
  );
};

GeoPointMap.propTypes = {
  ...GeoPointMapCanvas.propTypes,
  expandable: PropTypes.bool,
};

GeoPointMap.defaultProps = {
  ...GeoPointMapCanvas.defaultProps,
  expandable: false,
};

export default GeoPointMap;
